import React, { useMemo, useState } from 'react';
import { calculateKatalagModel, fmt } from '../lib/multiplierMath';
import type { MultiplierLever, MultiplierStep } from '../types';

type Props = {
  multiplierSteps: MultiplierStep[];
  multiplierLevers: MultiplierLever[];
};

export default function MultiplierEngine({ multiplierSteps, multiplierLevers }: Props) {
  const [discountPct, setDiscountPct] = useState(15);
  const [commissionPct, setCommissionPct] = useState(15);
  const price = 120;

  const katalag = useMemo(
    () => calculateKatalagModel({ price, discountPct, commissionPct }),
    [commissionPct, discountPct],
  );

  const multiplierInputs = [
    {
      title: 'Store inputs',
      items: [
        { label: 'Price', value: '$120.00', note: 'starting price' },
        { label: 'Additional Costs', value: '$33.30', note: 'CAC = $30.00, Processing = $3.30' },
        {
          label: 'Initial Net Earnings',
          value: '$86.70',
          note: 'what is left after direct, additional costs',
        },
        {
          label: 'Effect',
          value: '0.787x',
          note: '0.90 × 0.94 × 0.93\nAcquisition Risk 0.90x × Upfront Payment Risk 0.94x × Relevance Risk 0.93x',
        },
        { label: 'Effective Net Earnings', value: '$68.21', note: '$86.70 × 0.78678' },
      ],
    },
    {
      title: 'Marketplace inputs',
      items: [
        { label: 'Price', value: '$120.00', note: 'starting price' },
        {
          label: 'Additional Costs',
          value: '$23.27',
          note: 'Referral Fee = $18.00, Fulfillment = $5.00, Placement = $0.27',
        },
        {
          label: 'Initial Net Earnings',
          value: '$96.73',
          note: 'what is left after direct, additional costs',
        },
        {
          label: 'Effect',
          value: '0.857x',
          note: '0.95 × 0.96 × 0.94\nExposure Inefficiency 0.95x × Reduced Pricing Power 0.96x × Diminishing Control 0.94x',
        },
        { label: 'Effective Net Earnings', value: '$82.92', note: '$96.73 × 0.85728' },
      ],
    },
    {
      title: 'Katalag inputs',
      items: [
        { label: 'Price', value: '$120.00', note: 'starting price' },
        {
          label: 'Additional Costs',
          value: fmt(katalag.additionalCosts),
          note: `Discount = ${discountPct}%\nCommission = ${commissionPct}%\nPlatform Fee = 7%\nProcessing = ${fmt(katalag.processing)}`,
        },
        {
          label: 'Initial Net Earnings',
          value: fmt(katalag.initialNetEarnings),
          note: 'what is left after direct, additional costs',
        },
        {
          label: 'Effect',
          value: `${katalag.effect.toFixed(3)}x`,
          note: `Buyer Action Boost ${katalag.buyerActionBoost.toFixed(2)}x × Seller Motivation Boost ${katalag.sellerMotivationBoost.toFixed(2)}x × Return Visibility Lift ${katalag.returnVisibilityLift.toFixed(2)}x`,
        },
        {
          label: 'Effective Net Earnings',
          value: fmt(katalag.effectiveNetEarnings),
          note: `${fmt(katalag.initialNetEarnings)} × ${katalag.effect.toFixed(5)}`,
        },
      ],
    },
  ];

  const whyCards = [
    {
      title: 'Store risk factors',
      items: [
        { label: 'Acquisition Risk', value: '0.90x', note: 'paid campaigns may miss target' },
        {
          label: 'Upfront Payment Risk',
          value: '0.94x',
          note: 'paying before proof reduces agility',
        },
        { label: 'Relevance Risk', value: '0.93x', note: 'attention fades fast' },
      ],
    },
    {
      title: 'Marketplace risk factors',
      items: [
        {
          label: 'Exposure Inefficiency',
          value: '0.95x',
          note: 'more traffic, but less true visibility',
        },
        {
          label: 'Reduced Pricing Power',
          value: '0.96x',
          note: 'price competes louder than brand and quality',
        },
        {
          label: 'Diminishing Control',
          value: '0.94x',
          note: 'the platform owns discovery more than you do',
        },
      ],
    },
    {
      title: 'Katalag multiplier factors',
      items: [
        {
          label: 'Buyer Action Boost',
          value: `${katalag.buyerActionBoost.toFixed(2)}x`,
          note: 'exclusive access to unique discounted brand deals makes conversion stronger',
        },
        {
          label: 'Seller Motivation Boost',
          value: `${katalag.sellerMotivationBoost.toFixed(2)}x`,
          note: 'stronger commission increases seller effort, featuring, and follow-through',
        },
        {
          label: 'Return Visibility Lift',
          value: `${katalag.returnVisibilityLift.toFixed(2)}x`,
          note: 'built-in reminders and ongoing Kloutforce presence keep attention alive longer',
        },
      ],
    },
  ];

  return (
    <section id="multiplier" className="bg-white text-slate-950 scroll-mt-32">
      <div className="mx-auto max-w-7xl px-6 py-24 md:py-28">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm uppercase tracking-[0.22em] text-sky-600">
            How the economics work
          </p>
          <h2 className="mt-4 text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">
            The multiplier is not a trick. It is what happens when incentives align.
          </h2>
          <p className="mt-4 text-lg leading-8 text-slate-600">
            Discount helps the buyer act. Commission motivates the seller. Trusted presence keeps
            the offer visible longer. Together, they allow the same product to perform better,
            without giving up ownership or control.
          </p>
        </div>
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {multiplierSteps.map((item) => (
            <div
              key={item.title}
              className="rounded-[30px] border border-slate-200 bg-slate-50 p-8 shadow-sm"
            >
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-700">
                {item.eyebrow}
              </div>
              <h3 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                {item.title}
              </h3>
              <p className="mt-4 leading-7 text-slate-600">{item.description}</p>
              <div className="mt-6 space-y-3">
                {item.points.map((point) => (
                  <div
                    key={point}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700"
                  >
                    {point}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-[32px] border border-slate-200 bg-slate-50 p-8 md:p-10">
          <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div>
              <p className="text-sm uppercase tracking-[0.22em] text-sky-600">
                The two levers you control
              </p>
              <h3 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
                Discount and commission, set by you
              </h3>
              <p className="mt-4 text-base leading-7 text-slate-600">
                Discount lowers friction for the buyer. Commission gives the seller a real reason to
                show up. You decide how generous to be on each side, based on what your margins and
                your relationships can support.
              </p>
            </div>
            <div className="grid gap-4">
              {multiplierLevers.map((lever) => (
                <div
                  key={lever.title}
                  className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <h4 className="text-lg font-semibold text-slate-950">{lever.title}</h4>
                    <div className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-sky-700">
                      {lever.value}
                    </div>
                  </div>
                  <p className="mt-2 text-sm leading-7 text-slate-600">{lever.description}</p>
                  <div className="mt-4 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    <span className="font-semibold text-slate-950">Lever range:</span> {lever.range}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-2">
          <div className="rounded-[32px] border border-slate-200 bg-slate-50 p-8 shadow-sm md:p-10">
            <h3 className="text-2xl font-semibold tracking-tight text-slate-950">
              Discount: helping the buyer act
            </h3>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              A clear discount lowers hesitation. The decision becomes easier to take, easier to
              share, and easier to repeat.
            </p>
            <div className="mt-6">
              <div className="mb-3 flex items-center justify-between text-sm font-semibold text-slate-950">
                <span>Discount</span>
                <span>{discountPct}%</span>
              </div>
              <input
                type="range"
                min="5"
                max="30"
                step="1"
                value={discountPct}
                onChange={(e) => setDiscountPct(Number(e.target.value))}
                className="w-full accent-sky-500"
                aria-label="Discount percentage"
              />
            </div>
            <div className="mt-6 space-y-3 rounded-[24px] border border-slate-200 bg-white p-6">
              <div className="flex items-center justify-between text-sm text-slate-700">
                <span>Base Price</span>
                <span className="font-bold text-slate-950">$120.00</span>
              </div>
              <div className="flex items-center justify-between text-sm text-slate-700">
                <span>Discount Amount</span>
                <span className="font-bold text-rose-600">{fmt(katalag.discountAmount)}</span>
              </div>
              <div className="flex items-center justify-between text-base text-slate-950">
                <span>Discounted Price</span>
                <span className="text-xl font-black">{fmt(katalag.discountedPrice)}</span>
              </div>
              <div className="flex items-center justify-between text-base text-slate-950">
                <span>Buyer Action Boost</span>
                <span className="text-xl font-black text-sky-600">
                  {katalag.buyerActionBoost.toFixed(2)}x
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-[32px] border border-slate-200 bg-slate-50 p-8 shadow-sm md:p-10">
            <h3 className="text-2xl font-semibold tracking-tight text-slate-950">
              Commission: motivating the seller
            </h3>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              A meaningful commission gives sellers a real reason to feature your offer, explain it
              well, and keep returning to it.
            </p>
            <div className="mt-6">
              <div className="mb-3 flex items-center justify-between text-sm font-semibold text-slate-950">
                <span>Commission</span>
                <span>{commissionPct}%</span>
              </div>
              <input
                type="range"
                min="5"
                max="25"
                step="1"
                value={commissionPct}
                onChange={(e) => setCommissionPct(Number(e.target.value))}
                className="w-full accent-sky-500"
                aria-label="Commission percentage"
              />
            </div>
            <div className="mt-6 space-y-3 rounded-[24px] border border-slate-200 bg-white p-6">
              <div className="flex items-center justify-between text-sm text-slate-700">
                <span>Commission</span>
                <span className="font-bold text-slate-950">{commissionPct}%</span>
              </div>
              <div className="flex items-center justify-between text-sm text-slate-700">
                <span>Commission Amount</span>
                <span className="font-bold text-slate-950">{fmt(katalag.commissionAmount)}</span>
              </div>
              <div className="flex items-center justify-between text-sm text-slate-700">
                <span>Platform Fee + Processing</span>
                <span className="font-bold text-slate-950">
                  {fmt(katalag.platformFee + katalag.processing)}
                </span>
              </div>
              <div className="flex items-center justify-between text-base text-slate-950">
                <span>Initial Net Earnings</span>
                <span className="text-xl font-black">{fmt(katalag.initialNetEarnings)}</span>
              </div>
              <div className="flex items-center justify-between text-base text-slate-950">
                <span>Seller Motivation Boost</span>
                <span className="text-xl font-black text-sky-600">
                  {katalag.sellerMotivationBoost.toFixed(2)}x
                </span>
              </div>
              <div className="flex items-center justify-between text-base text-slate-950">
                <span>Return Visibility Lift</span>
                <span className="text-xl font-black text-sky-600">
                  {katalag.returnVisibilityLift.toFixed(2)}x
                </span>
              </div>
              <div className="flex items-center justify-between text-base text-slate-950">
                <span>Effective Net Earnings</span>
                <span className="text-xl font-black">{fmt(katalag.effectiveNetEarnings)}</span>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                Math: {fmt(katalag.initialNetEarnings)} × {katalag.buyerActionBoost.toFixed(2)} ×{' '}
                {katalag.sellerMotivationBoost.toFixed(2)} ×{' '}
                {katalag.returnVisibilityLift.toFixed(2)} = {fmt(katalag.effectiveNetEarnings)}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <p className="mb-8 text-sm font-semibold uppercase tracking-[0.24em] text-sky-600">
            Inputs
          </p>
          <div className="grid gap-6 lg:grid-cols-3">
            {multiplierInputs.map((card) => (
              <div
                key={card.title}
                className="rounded-[30px] border border-slate-200 bg-slate-50 p-6 shadow-sm"
              >
                <div className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-500">
                  {card.title}
                </div>
                <div className="mt-5 space-y-4">
                  {card.items.map((item) => (
                    <div
                      key={item.label}
                      className="whitespace-pre-line rounded-2xl border border-slate-200 bg-white p-4"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-slate-950">{item.label}</div>
                        <div className="text-lg font-black text-slate-950">{item.value}</div>
                      </div>
                      <div className="mt-1 text-sm leading-6 text-slate-600">{item.note}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10">
          <p className="mb-8 text-sm font-semibold uppercase tracking-[0.24em] text-sky-600">
            Why the numbers improve
          </p>
          <div className="grid gap-6 lg:grid-cols-3">
            {whyCards.map((card) => (
              <div
                key={card.title}
                className="rounded-[30px] border border-slate-200 bg-slate-50 p-6 shadow-sm"
              >
                <div className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-500">
                  {card.title}
                </div>
                <div className="mt-5 space-y-4">
                  {card.items.map((item) => (
                    <div
                      key={item.label}
                      className="rounded-2xl border border-slate-200 bg-white p-4"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-slate-950">{item.label}</div>
                        <div className="text-lg font-black text-slate-950">{item.value}</div>
                      </div>
                      <div className="mt-1 text-sm leading-6 text-slate-600">{item.note}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 rounded-[30px] border border-sky-200 bg-sky-50 p-8 text-center">
          <p className="text-sm uppercase tracking-[0.22em] text-sky-700">In one line</p>
          <p className="mx-auto mt-3 max-w-4xl text-2xl font-semibold leading-tight tracking-tight text-slate-950 md:text-3xl">
            Keep your store as your home base. Let trusted people help the right customers find it.
          </p>
        </div>
      </div>
    </section>
  );
}
